/**
 * Notification Sheet Panel
 * 
 * Slide-in sheet panel that displays notifications with tabs for filtering.
 * Includes All/Unread tabs and settings button to open preferences modal.
 */

import { useState, useMemo } from "react";
import { Settings, CheckCheck } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications } from "./use-notifications";
import { NotificationCard } from "./notification-card";
import { cn } from "@/lib/utils";

type TabValue = "all" | "unread";

export const NotificationSheet = () => {
  const {
    isSheetOpen,
    closeSheet,
    notifications,
    isLoadingNotifications,
    markAsRead,
    markAllAsRead,
    openPreferencesModal,
  } = useNotifications();
  
  const [activeTab, setActiveTab] = useState<TabValue>("all");
  
  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    if (activeTab === "unread") {
      return notifications.filter((n) => !n.read);
    }
    return notifications;
  }, [notifications, activeTab]);
  
  const handleNotificationClick = (id: string, isRead: boolean) => {
    if (!isRead) {
      markAsRead(id);
    }
  };
  
  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };
  
  return (
    <Sheet open={isSheetOpen} onOpenChange={closeSheet}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] p-0 flex flex-col h-full">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 px-6 py-4 border-b">
          <div className="flex items-center justify-between gap-4">
            <SheetTitle>Notifications</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={openPreferencesModal}
              className="h-8 w-8"
              aria-label="Notification settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>
        
        {/* Tabs */}
        <div className="flex-shrink-0 flex items-center gap-1 px-6 py-3 border-b">
          <button
            onClick={() => setActiveTab("all")}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
              activeTab === "all"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab("unread")}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
              activeTab === "unread"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            Unread
          </button>
        </div>
        
        {/* Content */}
        <ScrollArea className="flex-1 min-h-0 px-6">
          {isLoadingNotifications ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-muted-foreground">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <CheckCheck className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">
                {activeTab === "unread" ? "No unread notifications" : "No notifications"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activeTab === "unread"
                  ? "You're all caught up!"
                  : "You'll see notifications here when events occur"}
              </p>
            </div>
          ) : (
            <div className="space-y-2 py-4">
              {filteredNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification.id, notification.read)}
                />
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer - Mark all as read button */}
        {filteredNotifications.length > 0 && filteredNotifications.some((n) => !n.read) && (
          <div className="flex-shrink-0 px-6 py-3 border-t bg-background">
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="w-full"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark all as read
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

